import { useState, type FormEvent } from 'react';
import { IconMapPin, IconSearch, IconStarFilled, IconX } from '@tabler/icons-react';
import type { ConnectionModalProps } from '@interfaces/Integration';
import type { GoogleBusinessConnectionResult } from './hooks/useGoogleBusinessConnection';

/**
 * Modal de vinculación de la ficha de Google.
 *
 * El comercio busca su negocio por nombre y elige de una lista, en vez de tener
 * que averiguar un `place_id` por su cuenta — que es lo que pediría un formulario
 * de credenciales y lo que ningún comercio sabe hacer.
 *
 * La ficha elegida se verifica contra Google ANTES de guardarse, así que no hay
 * forma de terminar con un toast verde sobre una vinculación que no existe.
 */

/**
 * `Card.tsx` monta este componente con el contrato compartido de ConnectionModal
 * (pensado para el QR de WhatsApp) más `connection`, que trae el hook completo.
 * De ahí salen los candidatos y las acciones de búsqueda/selección: montar un
 * segundo hook aquí trabajaría sobre un estado distinto al de la tarjeta.
 */
type Props = ConnectionModalProps;

/** Google marca así las fichas que ya no operan. Vincularlas casi siempre es un error. */
const CLOSED_STATUSES = ['CLOSED_PERMANENTLY', 'CLOSED_TEMPORARILY'];

const STATUS_LABELS: Record<string, string> = {
  CLOSED_PERMANENTLY: 'Cerrado permanentemente',
  CLOSED_TEMPORARILY: 'Cerrado temporalmente',
};

/**
 * A partir de aquí se considera que la ficha podría ser de otra sucursal.
 *
 * 1 km: las dos sucursales que motivaron esto están a ~2.8 km, y un domicilio
 * registrado rara vez se desvía más de unas cuadras de su propia ficha. Es un
 * aviso, no un bloqueo — hay negocios legítimamente lejos de su dirección
 * registrada (plazas comerciales, fichas con la dirección mal puesta en Google).
 */
const FAR_THRESHOLD_M = 1000;

const isFar = (distanceM: number | null): distanceM is number => distanceM !== null && distanceM > FAR_THRESHOLD_M;

const formatDistance = (distanceM: number | null): string => {
  if (distanceM === null) return '';
  // Bajo 1 km en metros redondeados a centenas: "a 300 m" se lee mejor que "a 0.3 km".
  if (distanceM < 1000) return `${Math.round(distanceM / 100) * 100} m`;
  return `${(distanceM / 1000).toFixed(1)} km`;
};

export const GoogleBusinessConnectModal = ({ isOpen, onClose, isVerifying, connection }: Props) => {
  const google = connection as GoogleBusinessConnectionResult | undefined;
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!isOpen || !google) return null;

  const { candidates, isSearching, isConnecting, searchPlaces, connectWithPlaceId } = google;
  const onSearch = searchPlaces;
  const onSelect = connectWithPlaceId;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSearch(query);
  };

  const canConnect = Boolean(selectedId) && !isConnecting && !isVerifying;
  const selectedCandidate = candidates.find((candidate) => candidate.place_id === selectedId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="google-connect-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-color-white shadow-xl">
        <header className="flex items-start justify-between border-b border-color-gray_light p-5">
          <div>
            <h2 id="google-connect-title" className="text-lg font-semibold text-color-primary">
              Vincula tu ficha de Google
            </h2>
            <p className="mt-1 text-sm text-color-gray_dark">
              Busca tu negocio como aparece en Google Maps y selecciónalo.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-color-gray_dark transition hover:bg-color-gray_light"
          >
            <IconX size={20} aria-hidden />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="border-b border-color-gray_light p-5">
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ej. Taquería El Regio, Monterrey"
              aria-label="Nombre del negocio"
              className="flex-1 rounded-lg border border-color-gray px-3 py-2 text-sm text-color-primary focus:border-color-primary focus:outline-none"
            />
            <button
              type="submit"
              disabled={isSearching || query.trim().length < 3}
              className="flex items-center gap-1 rounded-lg bg-color-primary px-4 py-2 text-sm font-semibold text-color-white transition hover:opacity-90 disabled:opacity-50"
            >
              <IconSearch size={16} aria-hidden />
              {isSearching ? 'Buscando…' : 'Buscar'}
            </button>
          </div>
          {/*
            Google descarta del ranking las fichas poco prominentes cuando hay
            otra de la misma marca con reseñas: buscar "Yeivi's Boneless" devolvía
            SOLO la sucursal Centro, y la del Capitolio —que tiene su propio
            place_id pero 0 reseñas— no aparecía. Agregar la ciudad es lo que la
            saca. Sin este aviso el comercio concluye que su ficha no existe, o
            peor, vincula la de otra sucursal.
          */}
          <p className="mt-2 text-xs text-color-gray_dark">
            Incluye la ciudad y la colonia o calle, sobre todo si tu negocio tiene varias sucursales o tu ficha es
            nueva. Ej. &quot;Tacos Keyon Cumbres, Monterrey&quot;.
          </p>
        </form>

        <div className="flex-1 overflow-y-auto p-5">
          {isSearching && <p className="py-6 text-center text-sm text-color-gray_dark">Buscando en Google…</p>}

          {!isSearching && candidates.length === 0 && (
            <p className="py-6 text-center text-sm text-color-gray_dark">Busca tu negocio para ver los resultados.</p>
          )}

          <ul className="flex flex-col gap-2">
            {candidates.map((candidate) => {
              const isSelected = selectedId === candidate.place_id;
              const isClosed = CLOSED_STATUSES.includes(candidate.business_status || '');

              return (
                <li key={candidate.place_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(candidate.place_id)}
                    aria-pressed={isSelected}
                    className={`flex w-full gap-3 rounded-lg border p-3 text-left transition ${
                      isSelected
                        ? 'border-color-primary bg-color-gray_ultra_light'
                        : 'border-color-gray_light hover:border-color-gray'
                    }`}
                  >
                    <IconMapPin size={18} className="mt-0.5 shrink-0 text-color-gray_dark" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-color-primary">{candidate.title}</p>
                      <p className="truncate text-xs text-color-gray_dark">{candidate.address}</p>

                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-color-gray_dark">
                        {candidate.rating !== null && (
                          <span className="flex items-center gap-1">
                            <IconStarFilled size={12} className="text-color-warning_yellow" aria-hidden />
                            {candidate.rating.toFixed(1)} ({candidate.user_rating_count})
                          </span>
                        )}
                        {/* Avisar del cierre evita el error más común: vincular una
                            ficha duplicada y vieja que Google ya marcó cerrada. */}
                        {isClosed && (
                          <span className="font-medium text-color-warning">
                            {STATUS_LABELS[candidate.business_status as string]}
                          </span>
                        )}
                        {/*
                          La distancia al domicilio registrado es la ÚNICA señal de
                          sucursal equivocada que no depende del ranking de Google
                          —que ya demostró colapsar a la sucursal más prominente
                          incluso con restricción geográfica—. Se muestra siempre
                          que haya dato, y se marca en rojo cuando está lejos.
                        */}
                        {isFar(candidate.distance_m) ? (
                          <span className="font-medium text-color-warning">
                            a {formatDistance(candidate.distance_m)} de tu dirección
                          </span>
                        ) : (
                          candidate.distance_m !== null && (
                            <span>a {formatDistance(candidate.distance_m)} de tu dirección</span>
                          )
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <footer className="border-t border-color-gray_light p-5">
          {/*
            Un botón deshabilitado sin explicación se lee como roto: pasó en
            pruebas —"le di vincular ficha y no pasó nada"— porque no había nada
            seleccionado y el clic no producía ninguna señal. El texto dice qué
            falta, y la línea de consecuencia contesta la otra duda que salió
            ahí mismo: "no sé exactamente qué pasa al vincular".
          */}
          {/*
            El aviso de ficha lejana va aquí y no solo en la lista porque este es
            el último punto antes de vincular. El caso real: un comercio del
            Capitolio quedó vinculado a la ficha de la sucursal Centro, a 2.8 km,
            sin que nada se lo advirtiera. Es aviso y no bloqueo: hay negocios
            legítimamente lejos de su dirección registrada.
          */}
          {isFar(selectedCandidate?.distance_m ?? null) && (
            <p className="mb-3 rounded-lg bg-color-gray_ultra_light p-3 text-xs font-medium text-color-warning">
              Esta ficha está a {formatDistance(selectedCandidate?.distance_m ?? null)} de la dirección registrada de tu
              negocio. Si tienes varias sucursales, revisa que sea la correcta antes de vincular.
            </p>
          )}
          <p className="mb-3 text-xs text-color-gray_dark">
            {selectedId
              ? 'Al vincular, Plick leerá los datos públicos de esta ficha (nombre, dirección, calificación y reseñas) para mostrarlos en tu panel. No modifica nada en Google, y puedes desvincularla cuando quieras.'
              : 'Busca tu negocio y selecciónalo de la lista para poder vincularlo.'}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-color-gray px-4 py-2 text-sm font-medium text-color-primary transition hover:bg-color-gray_light"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => selectedId && onSelect(selectedId)}
              disabled={!canConnect}
              className="rounded-lg bg-color-primary px-4 py-2 text-sm font-semibold text-color-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isConnecting ? 'Vinculando…' : 'Vincular ficha'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};
